/**
 * Live Wall — Recorded mode. Up to six cameras playing back the SAME moment, on one timeline.
 *
 * Shared by both dashboards: the local node's Live Wall and the cloud's per-site one. The data
 * comes through `useApi().wallPlayback`, which each app binds to its own recordings endpoints
 * (the cloud's through the tunnel), so this page never knows which node it is on.
 *
 *   ┌ toolbar ─────────── Live | Recorded ──────────────┐
 *   │ cameras │  ┌───────────┬─────┐                     │
 *   │ (drag)  │  │    big    │  2  │   the "1 + 5" shape │
 *   │         │  │           ├─────┤   (3×3, r1c1..r2c2  │
 *   │         │  │           │  3  │    merged)          │
 *   │         │  ├─────┬─────┼─────┤                     │
 *   │         │  │  4  │  5  │  6  │                     │
 *   ├ time+date · ‹date› · range · select · − 5m…All + ········ ⏮ «30 «10 ▶ 10» 30» ⏭ · −1×+ ┤
 *   │ one lane per camera: footage by trigger, event / motion windows on top  │
 *
 * How the six stay together: ONE clock (usePlaybackClock) that every tile follows through its
 * own player-time ↔ wall-clock table — see PlaybackWallTile. A seek anywhere is a seek of the
 * clock. The clock waits for a tile that is still loading (bounded, so one stuck camera does not
 * freeze the wall) and skips stretches where no camera recorded anything.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FilmIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useApi } from '../api/ApiProvider';
import type { CameraRef, Group, RecordingSegment, RecordingTriggerWindow } from '../api/contracts';
import ToolbarIdentity from '../components/common/ToolbarIdentity';
import { Tip } from '../components/common/Tooltip';
import WallModeToggle from '../components/wall-playback/WallModeToggle';
import PlaybackWallTile, { type TileStatus } from '../components/wall-playback/PlaybackWallTile';
import MultiCameraTimeline, { type TimelineLane } from '../components/wall-playback/MultiCameraTimeline';
import PlaybackCameraPanel from '../components/wall-playback/PlaybackCameraPanel';
import TimeRangePicker from '../components/recordings/TimeRangePicker';
import {
  ClockReadout, DateStepper, TransportCluster, jumpPointsFor, jumpTarget, usePlaybackKeys, footageRangeOf,
} from '../components/recordings/PlaybackControls';
import { usePlaybackClock, PLAYBACK_SPEEDS } from '../components/wall-playback/usePlaybackClock';
import {
  WALL_PLAYBACK_MAX, liveWallHref, parseCameraIds, slotsFromIds, idsFromSlots, wallPlaybackWindows,
  type WallPlaybackSlot,
} from '../components/wall-playback/wallPlaybackLayout';
import { buildOffsetTable, footageAt, nextFootageAfter, type OffsetRow } from '../components/recordings/playbackOffsets';

export interface WallPlaybackPageProps {
  /** The live wall's route — where the toggle's "Live" goes. */
  livePath: string;
  /** Cloud: the site in the title. */
  siteName?: string;
  /** Cloud with no site picked: render the "pick a site" state instead of querying. */
  blocked?: boolean;
  /** Changing it (the selected node, on cloud) resets the wall — another site's cameras. */
  nodeKey?: string;
  className?: string;
}

/** One colour per WINDOW, shared by the tile's dot and its timeline lane. */
const WINDOW_ACCENTS = ['#60a5fa', '#f472b6', '#34d399', '#fb923c', '#a78bfa', '#facc15'];

/** How long the clock waits on one tile before letting it catch up on its own. */
const HOLD_LOADING_MS = 30_000;
const HOLD_BUFFERING_MS = 12_000;
/** How often today's segment lists are re-read. Segments finalize every 5 minutes. */
const SEGMENT_REFRESH_MS = 60_000;

const PANEL_KEY = 'trinetra.wallPlayback.panelCollapsed';

const pad = (n: number) => String(n).padStart(2, '0');
const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const isDate = (s: string | null): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
const isHM = (s: string | null): s is string => !!s && /^\d{2}:\d{2}$/.test(s);
function midnightOf(date: string) {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}
const hmToMs = (hm: string) => { const [h, m] = hm.split(':').map(Number); return (h * 60 + (m || 0)) * 60000; };

export default function WallPlaybackPage({ livePath, siteName, blocked = false, nodeKey, className = '' }: WallPlaybackPageProps) {
  const api = useApi().wallPlayback;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const viewId = params.get('view');

  // ─── Catalogue ───
  const [cameras, setCameras] = useState<CameraRef[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const nameOf = useMemo(() => {
    const m = new Map(cameras.map((c) => [String(c.id), c.name]));
    return (id: string) => m.get(id) || id;
  }, [cameras]);

  // ─── The wall: 9 cells, six of them windows ───
  const windows = useMemo(() => wallPlaybackWindows(), []);
  const [slots, setSlots] = useState<(WallPlaybackSlot | null)[]>(() => slotsFromIds(parseCameraIds(params.get('cameras')), (id) => id));

  // ─── Date + range ───
  const [date, setDate] = useState(() => (isDate(params.get('date')) ? params.get('date')! : todayLocal()));
  const [range, setRange] = useState(() => ({
    start: isHM(params.get('from')) ? params.get('from')! : '00:00',
    end: isHM(params.get('to')) ? params.get('to')! : '23:59',
  }));
  // Once the wall's segments for a day are in: fit the range to the footage (unless the URL
  // named a range — someone asking for exactly that) and put the clock on the first frame.
  const pendingRef = useRef({ fit: !(isHM(params.get('from')) && isHM(params.get('to'))), seek: true });
  // Mirrors pendingRef.fit as state so tiles can wait for it. A tile that built its source
  // on the default 00:00–23:59 range fetched a whole-day playlist, then fetched it AGAIN
  // when the fit narrowed the range — each one megabytes through the cloud tunnel, and the
  // first player (with whatever transcode it had started) thrown away.
  const [fitPending, setFitPending] = useState(() => pendingRef.current.fit);
  // True while the range is the page's own fit (not one the viewer chose or the URL named), so
  // it may grow to keep up with footage recorded after the page opened.
  const autoRangeRef = useRef(pendingRef.current.fit);

  const dayStart = midnightOf(date);
  const rangeStartMs = dayStart + hmToMs(range.start);
  const rangeEndMs = Math.max(rangeStartMs + 60_000, dayStart + hmToMs(range.end));

  // ─── Segments, per camera, for the whole day ───
  const [segments, setSegments] = useState<Record<string, RecordingSegment[] | undefined>>({});
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
  // Event / motion windows, per camera, for the whole day — colour on the timeline + the
  // previous / next event buttons. Best-effort: a failure is just an empty list.
  const [triggers, setTriggers] = useState<Record<string, RecordingTriggerWindow[] | undefined>>({});

  // Camera list folded to a rail — per viewer, remembered in this browser only.
  const [panelCollapsed, setPanelCollapsed] = useState(() => {
    try { return window.localStorage.getItem(PANEL_KEY) === '1'; } catch { return false; }
  });
  const togglePanel = useCallback(() => {
    setPanelCollapsed((c) => {
      try { window.localStorage.setItem(PANEL_KEY, c ? '0' : '1'); } catch { /* private mode */ }
      return !c;
    });
  }, []);

  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const say = useCallback((msg: string) => {
    setToast(msg);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 3500);
  }, []);

  // A new site is a different set of cameras: the wall starts over.
  const firstNodeKey = useRef(nodeKey);
  useEffect(() => {
    if (firstNodeKey.current === nodeKey) return;
    firstNodeKey.current = nodeKey;
    setSlots(slotsFromIds([], (id) => id));
    setSegments({});
    setTriggers({});
    setAvailableDates(new Set());
  }, [nodeKey]);

  useEffect(() => {
    if (blocked) { setCatalogLoading(false); return undefined; }
    let cancelled = false;
    setCatalogLoading(true);
    Promise.all([api.listCameras(), api.listGroups().catch(() => [] as Group[])])
      .then(([cams, grps]) => {
        if (cancelled) return;
        setCameras(cams);
        setGroups(grps);
        // Cameras the viewer cannot see (or that no longer exist) come off the wall rather than
        // sitting there as an id that 403s.
        const known = new Set(cams.map((c) => String(c.id)));
        setSlots((prev) => prev.map((s) => (s && known.has(s.cameraId) ? s : null)));
      })
      .catch(() => { if (!cancelled) say('Could not load cameras'); })
      .finally(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; };
  }, [api, blocked, nodeKey, say]);

  const wallIds = useMemo(() => idsFromSlots(slots), [slots]);
  const wallIdsKey = wallIds.join(',');

  // Keep the URL in step — a reload, or the link, lands on this same wall, day and range.
  useEffect(() => {
    const next = new URLSearchParams();
    if (wallIds.length) next.set('cameras', wallIds.join(','));
    if (viewId) next.set('view', viewId);
    next.set('date', date);
    next.set('from', range.start);
    next.set('to', range.end);
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallIdsKey, viewId, date, range.start, range.end]);

  // New day → every camera's segments are stale.
  const segDateRef = useRef(date);
  useEffect(() => {
    if (segDateRef.current !== date) { segDateRef.current = date; setSegments({}); setTriggers({}); }
  }, [date]);

  // Fetch whatever the wall is missing.
  useEffect(() => {
    if (blocked) return;
    const fromIso = new Date(dayStart).toISOString();
    const toIso = new Date(dayStart + 86_400_000 - 1).toISOString();
    for (const id of wallIds) {
      if (id in segments) continue;
      setSegments((prev) => (id in prev ? prev : { ...prev, [id]: undefined }));
      api.getSegments(id, fromIso, toIso)
        .then((segs) => { if (segDateRef.current === date) setSegments((prev) => ({ ...prev, [id]: segs })); })
        .catch(() => { if (segDateRef.current === date) setSegments((prev) => ({ ...prev, [id]: [] })); });
      api.getTriggers(id, fromIso, toIso)
        .then((rows) => { if (segDateRef.current === date) setTriggers((prev) => ({ ...prev, [id]: rows })); })
        .catch(() => { if (segDateRef.current === date) setTriggers((prev) => ({ ...prev, [id]: [] })); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallIdsKey, date, blocked, api, segments]);

  // Today's footage keeps arriving: a segment is finalized every 5 minutes. The fetch above runs
  // once per camera, so without this nothing recorded after the page opened ever reached the
  // timeline or a playlist — it read as a gap until a reload. Re-read while today is on the
  // wall; an unchanged answer is dropped so tiles and the timeline don't re-render for nothing.
  useEffect(() => {
    if (blocked || !wallIds.length || date !== todayLocal()) return undefined;
    const fromIso = new Date(dayStart).toISOString();
    const toIso = new Date(dayStart + 86_400_000 - 1).toISOString();
    const sig = (list?: RecordingSegment[]) => {
      const last = list && list.length ? list[list.length - 1] : undefined;
      return list ? `${list.length}|${last?.id}|${last?.status}|${last?.end_time}` : '';
    };
    const iv = window.setInterval(() => {
      for (const id of wallIds) {
        api.getSegments(id, fromIso, toIso)
          .then((segs) => {
            if (segDateRef.current !== date) return;
            setSegments((prev) => (sig(prev[id]) === sig(segs) ? prev : { ...prev, [id]: segs }));
          })
          .catch(() => { /* keep what we have; next tick retries */ });
        api.getTriggers(id, fromIso, toIso)
          .then((rows) => { if (segDateRef.current === date) setTriggers((prev) => ({ ...prev, [id]: rows })); })
          .catch(() => { /* best-effort, as above */ });
      }
    }, SEGMENT_REFRESH_MS);
    return () => window.clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallIdsKey, date, blocked, api]);

  // Days with footage on any camera on the wall — the ‹ › buttons jump between them.
  useEffect(() => {
    if (blocked || !wallIds.length) return undefined;
    let cancelled = false;
    Promise.all(wallIds.map((id) => api.getAvailableDates(id).catch(() => [] as string[])))
      .then((lists) => { if (!cancelled) setAvailableDates(new Set(lists.flat())); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallIdsKey, blocked, api, nodeKey]);

  // ─── Offset tables per camera, for the gap skip ───
  const tables = useMemo(() => {
    const out: OffsetRow[][] = [];
    for (const id of wallIds) {
      const s = segments[id];
      if (s) out.push(buildOffsetTable(s, rangeStartMs, rangeEndMs));
    }
    return out;
  }, [wallIds, segments, rangeStartMs, rangeEndMs]);
  const tablesRef = useRef(tables);
  tablesRef.current = tables;

  // ─── Tile states → the clock's hold ───
  const statusRef = useRef(new Map<number, { status: TileStatus; since: number }>());
  const [statusVer, setStatusVer] = useState(0);
  const onStatus = useCallback((position: number, status: TileStatus) => {
    statusRef.current.set(position, { status, since: Date.now() });
    setStatusVer((v) => v + 1);
  }, []);
  const [waiting, setWaiting] = useState(0);
  const isHeld = useCallback(() => {
    const now = Date.now();
    let n = 0;
    for (const { status, since } of statusRef.current.values()) {
      if (status === 'loading' && now - since < HOLD_LOADING_MS) n++;
      else if (status === 'buffering' && now - since < HOLD_BUFFERING_MS) n++;
    }
    return n > 0;
  }, []);

  const skipGap = useCallback((ms: number) => {
    const ts = tablesRef.current;
    if (!ts.length) return undefined;
    if (ts.some((rows) => footageAt(rows, ms))) return undefined;
    let next: number | null = null;
    for (const rows of ts) {
      const r = nextFootageAfter(rows, ms);
      if (r && (next === null || r.wallStart < next)) next = r.wallStart;
    }
    return next;
  }, []);

  const clock = usePlaybackClock({ minMs: rangeStartMs, maxMs: rangeEndMs, isHeld, skipGap });

  // How many tiles the clock is waiting on — said under the transport, so a pause is explained.
  useEffect(() => {
    if (!clock.held) { setWaiting(0); return; }
    let n = 0;
    for (const { status } of statusRef.current.values()) if (status === 'loading' || status === 'buffering') n++;
    setWaiting(n);
  }, [clock.held, statusVer]);

  // ─── Fit the range to the footage, then start at its first frame ───
  const earliestFootage = useCallback((fromMs: number, toMs: number) => {
    let best: number | null = null;
    for (const id of wallIds) {
      for (const s of segments[id] || []) {
        const st = Math.max(new Date(s.start_time).getTime(), fromMs);
        if (new Date(s.end_time).getTime() > fromMs && st < toMs && (best === null || st < best)) best = st;
      }
    }
    return best;
  }, [wallIds, segments]);

  // A seek that must land in a range being set in the same breath. The clock clamps to the
  // range it was RENDERED with, so the seek waits for the new one to commit.
  const seekAfterRangeRef = useRef<number | null>(null);
  const seekInRange = useCallback((ms: number, next: { start: string; end: string }) => {
    if (next.start === range.start && next.end === range.end) clock.seek(ms);
    else { seekAfterRangeRef.current = ms; setRange(next); }
  }, [range.start, range.end, clock]);
  useEffect(() => {
    if (seekAfterRangeRef.current === null) return;
    clock.seek(seekAfterRangeRef.current);
    seekAfterRangeRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStartMs, rangeEndMs]);

  const lastDateRef = useRef(date);
  useEffect(() => {
    if (lastDateRef.current === date) return;
    lastDateRef.current = date;
    pendingRef.current = { fit: true, seek: true };
    setFitPending(true);
  }, [date]);
  useEffect(() => {
    const pending = pendingRef.current;
    if ((!pending.fit && !pending.seek) || !wallIds.length) return;
    if (wallIds.some((id) => segments[id] === undefined)) return;
    pendingRef.current = { fit: false, seek: false };
    // Batched with the setRange below, so tiles see the fitted range and the go-ahead together.
    if (pending.fit) setFitPending(false);
    if (!pending.fit) {
      const first = earliestFootage(rangeStartMs, rangeEndMs);
      if (first !== null) clock.seek(first);
      return;
    }
    const all = wallIds.flatMap((id) => segments[id] || []).filter((s) => (s.status || 'finalized') === 'finalized' || s.status === 'in-progress');
    if (!all.length) { setRange({ start: '00:00', end: '23:59' }); return; }
    const minStart = new Date(Math.min(...all.map((s) => new Date(s.start_time).getTime())));
    const maxEnd = new Date(Math.max(...all.map((s) => Math.max(new Date(s.end_time).getTime(), s.status === 'in-progress' ? Date.now() : 0))));
    const sameDay = (d: Date) => d.getTime() >= dayStart && d.getTime() < dayStart + 86_400_000;
    const fs = sameDay(minStart) ? `${pad(minStart.getHours())}:00` : '00:00';
    const endH = maxEnd.getMinutes() > 0 || maxEnd.getSeconds() > 0 ? maxEnd.getHours() + 1 : maxEnd.getHours();
    let fe = !sameDay(maxEnd) || endH >= 24 ? '23:59' : `${pad(endH)}:00`;
    if (fe <= fs) fe = '23:59';
    const first = earliestFootage(dayStart + hmToMs(fs), dayStart + hmToMs(fe));
    autoRangeRef.current = true;
    seekInRange(first ?? dayStart + hmToMs(fs), { start: fs, end: fe });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, wallIdsKey, date]);

  // An auto-fitted range ends at the hour after the newest footage — fine when the page opens,
  // but today's footage grows past it. Stretch the end to keep up; a range the viewer chose is
  // theirs and stays put. (The tiles rebuild their playlists for the new range at the clock.)
  useEffect(() => {
    if (!autoRangeRef.current || fitPending || date !== todayLocal()) return;
    const all = wallIds.flatMap((id) => segments[id] || []);
    if (!all.length) return;
    const maxEndMs = Math.max(...all.map((s) => Math.max(
      new Date(s.end_time).getTime(), s.status === 'in-progress' ? Date.now() : 0)));
    if (maxEndMs <= rangeEndMs) return;
    const maxEnd = new Date(maxEndMs);
    const endH = maxEnd.getMinutes() > 0 || maxEnd.getSeconds() > 0 ? maxEnd.getHours() + 1 : maxEnd.getHours();
    const fe = maxEndMs >= dayStart + 86_400_000 || endH >= 24 ? '23:59' : `${pad(endH)}:00`;
    if (fe > range.end) setRange((r) => ({ ...r, end: fe }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, fitPending]);

  // ─── Wall edits ───
  const assign = useCallback((position: number, cam: { id: string; name: string }) => {
    statusRef.current.delete(position);
    setSlots((prev) => {
      const next = [...prev];
      const from = next.findIndex((s) => s?.cameraId === cam.id);
      const displaced = next[position];
      if (from === position) return prev;
      // Already elsewhere on the wall → the two windows trade places.
      if (from >= 0) next[from] = displaced ? { ...displaced, gridPosition: from } : null;
      next[position] = { cameraId: cam.id, cameraName: cam.name, gridPosition: position };
      return next;
    });
  }, []);
  const swap = useCallback((a: number, b: number) => {
    statusRef.current.delete(a);
    statusRef.current.delete(b);
    setSlots((prev) => {
      const next = [...prev];
      const A = next[a];
      const B = next[b];
      next[a] = B ? { ...B, gridPosition: a } : null;
      next[b] = A ? { ...A, gridPosition: b } : null;
      return next;
    });
  }, []);
  const remove = useCallback((position: number) => {
    setSlots((prev) => { const next = [...prev]; next[position] = null; return next; });
    statusRef.current.delete(position);
  }, []);
  const addToFirstEmpty = useCallback((cam: { id: string; name: string }) => {
    if (slots.some((s) => s?.cameraId === cam.id)) { say(`${cam.name} is already on the wall`); return; }
    const w = windows.find((x) => !slots[x.position]);
    if (!w) { say(`All ${WALL_PLAYBACK_MAX} windows are in use — drag onto a window to replace one`); return; }
    assign(w.position, cam);
  }, [slots, windows, assign, say]);
  const clearWall = () => { setSlots(slotsFromIds([], (id) => id)); statusRef.current.clear(); clock.pause(); };

  // ─── Previous / next event (shared with the Recordings page) ───
  const jumpPoints = useMemo(
    () => jumpPointsFor(wallIds.map((id) => segments[id]), wallIds.map((id) => triggers[id]), rangeStartMs, rangeEndMs),
    [wallIds, triggers, segments, rangeStartMs, rangeEndMs],
  );
  const jump = useCallback((dir: 1 | -1) => {
    const target = jumpTarget(jumpPoints.points, clock.timeRef.current, dir, rangeStartMs);
    if (target === null) { say(`No ${dir > 0 ? 'later' : 'earlier'} ${jumpPoints.kind} in this range`); return; }
    clock.seek(target);
  }, [jumpPoints, clock, rangeStartMs, say]);

  usePlaybackKeys({ toggle: clock.toggle, skip: clock.skip, jump });

  // ─── Date navigation ───
  const sortedDates = useMemo(() => [...availableDates].sort(), [availableDates]);
  const prevDate = sortedDates.filter((d) => d < date).pop() || null;
  const nextDate = sortedDates.find((d) => d > date) || null;
  const changeDate = (d: string) => { if (isDate(d) && d !== date) { clock.pause(); setDate(d); } };
  /** A timeline selection becomes the range — whole minutes, since the range is HH:mm. */
  const playRange = (startMs: number, endMs: number) => {
    const s = new Date(Math.max(dayStart, startMs));
    const e = new Date(Math.min(dayStart + 86_400_000 - 60_000, endMs + 59_999));
    const next = { start: `${pad(s.getHours())}:${pad(s.getMinutes())}`, end: `${pad(e.getHours())}:${pad(e.getMinutes())}` };
    if (!(next.start < next.end)) { say('Select at least a minute'); return; }
    seekInRange(Math.max(startMs, dayStart + hmToMs(next.start)), next);
    if (!clock.playing) clock.play();
  };
  const applyRange = (next: { start: string; end: string }) => {
    if (!(next.start < next.end)) { say('The range must end after it starts'); return; }
    const s = dayStart + hmToMs(next.start);
    const first = earliestFootage(s, dayStart + hmToMs(next.end));
    autoRangeRef.current = false;
    seekInRange(first ?? s, next);
  };

  const lanes: TimelineLane[] = useMemo(() => windows
    .map((w, i) => ({ w, i, s: slots[w.position] }))
    .filter((x) => x.s)
    .map(({ w, i, s }) => ({
      key: s!.cameraId,
      label: nameOf(s!.cameraId),
      color: WINDOW_ACCENTS[i],
      segments: segments[s!.cameraId],
      triggers: triggers[s!.cameraId],
      status: statusRef.current.get(w.position)?.status,
    })),
  // statusVer: lane status text reads the ref.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [windows, slots, segments, triggers, nameOf, statusVer]);

  const getAuthToken = useCallback(() => api.playbackAuthToken(), [api]);
  const playbackUrl = useCallback((c: string, f: string, t: string) => api.playbackUrl(c, f, t), [api]);
  const onWall = useMemo(() => new Set(wallIds), [wallIds]);
  const hasFootageAnywhere = tables.some((t) => t.length > 0);

  if (blocked) {
    return (
      <div className={`flex h-full flex-col items-center justify-center text-muted-foreground ${className}`}>
        <FilmIcon className="mb-3 h-12 w-12 opacity-30" />
        <p className="text-sm">Pick a site to play back its recordings</p>
      </div>
    );
  }

  // 1 selected date/time · 2 date · 3 time range — then the timeline's range selection + duration.
  const leading = (
    <>
      <ClockReadout timeMs={clock.time} note={waiting > 0 ? `waiting ${waiting}…` : null} noteTone="warn" />
      <DateStepper date={date} max={todayLocal()} onChange={changeDate} prevDate={prevDate} nextDate={nextDate} />
      {/* Pick From then To, confirm with OK — or drag a range on the timeline with Select. */}
      <TimeRangePicker value={range} onApply={applyRange} footage={footageRangeOf(wallIds.map((id) => segments[id]), date)} />
    </>
  );

  // 4 play / pause · 5 skip · 6 fast forward.
  const trailing = (
    <TransportCluster
      playing={clock.playing}
      onToggle={clock.toggle}
      disabled={!hasFootageAnywhere}
      onSkip={clock.skip}
      onPrevEvent={() => jump(-1)}
      onNextEvent={() => jump(1)}
      eventKind={jumpPoints.kind}
      playbackRate={clock.rate}
      speeds={PLAYBACK_SPEEDS}
      onRateChange={clock.setRate}
    />
  );

  return (
    <div className={`flex h-full min-h-0 flex-col gap-2 ${className}`}>
      {/* ─── Toolbar — the live wall's three zones, so switching modes moves nothing ─── */}
      <div className="flex flex-shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 sm:px-3 sm:py-2">
        <div className="flex min-w-0 flex-1 items-center">
          <ToolbarIdentity
            icon={FilmIcon}
            kind="Live Wall:"
            title={siteName ? `Recorded · ${siteName}` : 'Recorded playback'}
            detail={`${wallIds.length} of ${WALL_PLAYBACK_MAX} cameras · drag cameras from the list onto a window`}
          />
        </div>
        <div className="flex flex-none items-center justify-center">
          <WallModeToggle mode="recorded" onChange={() => navigate(liveWallHref(livePath, viewId))} />
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
          <Tip side="bottom" label={wallIds.length ? 'Take every camera off the wall' : 'The wall is already empty'}>
            <button
              onClick={clearWall}
              disabled={!wallIds.length}
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40 sm:text-sm"
            >
              <TrashIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          </Tip>
        </div>
      </div>

      {/* ─── Cameras | wall ─── */}
      <div className="flex min-h-0 flex-1 gap-2">
        <PlaybackCameraPanel
          className="hidden md:block"
          cameras={cameras as any}
          groups={groups as any}
          loading={catalogLoading}
          onWall={onWall}
          onAdd={addToFirstEmpty}
          collapsed={panelCollapsed}
          onToggleCollapsed={togglePanel}
        />
        <div
          className="grid min-h-0 min-w-0 flex-1 gap-1.5"
          style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gridTemplateRows: 'repeat(3, minmax(0, 1fr))' }}
        >
          {windows.map((w, i) => {
            const s = slots[w.position];
            return (
              <PlaybackWallTile
                // Keyed by camera, not window: a camera moved to another window keeps its player
                // (and whatever it had buffered) instead of reloading.
                key={s ? `cam-${s.cameraId}` : `empty-${w.position}`}
                window={w}
                camera={s ? { cameraId: s.cameraId, cameraName: nameOf(s.cameraId) } : null}
                segments={s ? segments[s.cameraId] : undefined}
                rangeStartMs={rangeStartMs}
                rangeEndMs={rangeEndMs}
                rangeSettled={!fitPending}
                clock={clock}
                playbackUrl={playbackUrl}
                getAuthToken={getAuthToken}
                tunnelled={api.tunnelled}
                accent={WINDOW_ACCENTS[i]}
                onStatus={onStatus}
                onDropCamera={assign}
                onDropWindow={swap}
                onRemove={remove}
              />
            );
          })}
        </div>
      </div>

      {/* ─── Timeline ─── */}
      <MultiCameraTimeline
        className="flex-none"
        lanes={lanes}
        rangeStartMs={rangeStartMs}
        rangeEndMs={rangeEndMs}
        timeMs={clock.time}
        onSeek={(ms) => clock.seek(ms)}
        onPlayRange={playRange}
        follow={clock.playing}
        leading={leading}
        trailing={trailing}
        dateLabel={new Date(dayStart).toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      />

      {toast && (
        <div className="fixed bottom-4 right-4 z-[60] rounded-lg bg-amber-500/90 px-4 py-2.5 text-sm font-medium text-white shadow-lg">{toast}</div>
      )}
    </div>
  );
}
